import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Memory() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [mems, setMems] = useState([]);
  const [tab, setTab] = useState("docs");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [memText, setMemText] = useState("");
  const [memType, setMemType] = useState("insight");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef();

  const loadAll = async () => {
    if (!user) return;
    try {
      const [d, m] = await Promise.all([api.get("/rag/documents"), api.get("/rag/memory")]);
      setDocs(d.data.items || []);
      setMems(m.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAll(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const ingestText = async () => {
    if (!text.trim() || !title.trim()) return toast.error("Title + text required");
    setBusy("ingest");
    try {
      const { data } = await api.post("/rag/ingest/text", { title, text, source: "manual" });
      toast.success(`Indexed · ${data.chunks} chunks`);
      setText(""); setTitle(""); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Ingest failed"); }
    finally { setBusy(null); }
  };

  const ingestFile = async (file) => {
    if (!file) return;
    setBusy("file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    try {
      const { data } = await api.post("/rag/ingest/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${data.chunks} chunks`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  const addMemory = async () => {
    if (!memText.trim()) return;
    setBusy("mem");
    try {
      await api.post("/rag/memory", { text: memText.trim(), memory_type: memType });
      toast.success("Memory saved");
      setMemText(""); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const search = async () => {
    if (!q.trim()) return;
    setBusy("search");
    try {
      const { data } = await api.post("/rag/search", { query: q, top_k: 5 });
      setResults(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setBusy(null); }
  };

  const delDoc = async (id) => {
    if (!confirm("Delete this document + all its chunks?")) return;
    try {
      await api.delete(`/rag/documents/${id}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const delMem = async (id) => {
    try {
      await api.delete(`/rag/memory/${id}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!user) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="memory-signin-required">
        <div className="font-serif-elite text-3xl mb-2">Long-Term Memory &amp; RAG</div>
        <div className="text-[color:var(--text-secondary)] text-sm">Sign in to build your personal research memory.</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="memory-page">
      <div className="border-b border-[color:var(--border-default)] pb-4">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ RAG · EMBEDDINGS · LONG-TERM MEMORY</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Your Memory</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Upload research PDFs, save personal insights, and semantic-search everything with vector embeddings.
        </p>
      </div>

      {/* Search bar */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid="rag-search">
        <div className="text-[10px] font-mono-data text-white tracking-widest mb-2">◆ SEMANTIC SEARCH</div>
        <div className="flex gap-2">
          <input data-testid="rag-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Ask across everything you've saved…" className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2 text-sm text-white placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--border-focus)]" />
          <button data-testid="rag-search-btn" onClick={search} disabled={busy === "search"} className="bg-white text-black px-4 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">{busy === "search" ? "…" : "SEARCH"}</button>
        </div>
        {results && (
          <div className="mt-3 space-y-3" data-testid="rag-results">
            {results.memory?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">MEMORY ({results.memory.length})</div>
                {results.memory.map((m, i) => (
                  <div key={`mem-${i}`} className="border border-[color:var(--border-default)] p-2 mb-1">
                    <div className="text-[10px] font-mono-data text-gold">SCORE {m.score.toFixed(3)} · {m.memory_type.toUpperCase()}</div>
                    <div className="text-xs text-white mt-1">{m.text}</div>
                  </div>
                ))}
              </div>
            )}
            {results.results?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">DOCUMENT CHUNKS ({results.results.length})</div>
                {results.results.map((r, i) => (
                  <div key={`chunk-${i}`} className="border border-[color:var(--border-default)] p-2 mb-1">
                    <div className="text-[10px] font-mono-data text-gold">SCORE {r.score.toFixed(3)} · CHUNK #{r.chunk_index}</div>
                    <div className="text-xs text-white mt-1 line-clamp-4">{r.text}</div>
                  </div>
                ))}
              </div>
            )}
            {(!results.memory?.length && !results.results?.length) && (
              <div className="text-xs text-[color:var(--text-muted)] font-mono-data">No matches — add some documents or memory first.</div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border border-[color:var(--border-default)] w-fit" data-testid="mem-tabs">
        <button data-testid="tab-docs" onClick={() => setTab("docs")} className={`px-4 py-2 text-[11px] font-mono-data tracking-widest border-r border-[color:var(--border-default)] ${tab === "docs" ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"}`}>DOCUMENTS ({docs.length})</button>
        <button data-testid="tab-memory" onClick={() => setTab("memory")} className={`px-4 py-2 text-[11px] font-mono-data tracking-widest ${tab === "memory" ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"}`}>MEMORY ({mems.length})</button>
      </div>

      {tab === "docs" && (
        <div className="space-y-3" data-testid="docs-panel">
          <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
            <div className="text-[10px] font-mono-data text-white tracking-widest mb-2">INGEST TEXT</div>
            <input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm text-white mb-2 focus:outline-none focus:border-[color:var(--border-focus)]" />
            <textarea data-testid="doc-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste research, notes, or transcripts…" rows={5} className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--border-focus)] resize-none" />
            <div className="flex gap-2 mt-2">
              <button data-testid="ingest-text-btn" onClick={ingestText} disabled={busy === "ingest"} className="bg-white text-black px-4 py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">{busy === "ingest" ? "INDEXING…" : "INDEX TEXT"}</button>
              <input ref={fileRef} type="file" accept=".pdf,.txt,.csv,.md" hidden onChange={(e) => ingestFile(e.target.files[0])} data-testid="doc-file-input" />
              <button data-testid="ingest-file-btn" onClick={() => fileRef.current?.click()} disabled={busy === "file"} className="border border-[color:var(--border-default)] text-white px-4 py-2 text-[11px] font-mono-data tracking-widest hover:bg-[color:var(--bg-hover)] disabled:opacity-40">{busy === "file" ? "UPLOADING…" : "UPLOAD PDF/TXT/CSV"}</button>
            </div>
          </div>
          <div className="border border-[color:var(--border-default)]">
            {docs.length === 0 ? (
              <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No documents indexed yet.</div>
            ) : (
              docs.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--border-default)] last:border-0" data-testid={`doc-${d.doc_id}`}>
                  <div>
                    <div className="text-sm text-white">{d.title}</div>
                    <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">{d.chunks} CHUNKS · {d.source?.toUpperCase()} · {(d.uploaded_at || "").slice(0, 10)}</div>
                  </div>
                  <button onClick={() => delDoc(d.doc_id)} className="text-[10px] font-mono-data text-bear border border-bear px-2 py-1 hover:bg-bear hover:text-white transition-colors" data-testid={`del-doc-${d.doc_id}`}>DELETE</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "memory" && (
        <div className="space-y-3" data-testid="memory-list-panel">
          <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
            <div className="text-[10px] font-mono-data text-white tracking-widest mb-2">ADD MEMORY</div>
            <div className="flex gap-2 mb-2">
              {["insight", "trade_setup", "preference", "note"].map((k) => (
                <button key={k} data-testid={`memtype-${k}`} onClick={() => setMemType(k)} className={`text-[10px] font-mono-data tracking-widest px-2 py-1 border ${memType === k ? "border-gold text-gold" : "border-[color:var(--border-default)] text-[color:var(--text-secondary)]"}`}>{k.toUpperCase()}</button>
              ))}
            </div>
            <textarea data-testid="mem-text" value={memText} onChange={(e) => setMemText(e.target.value)} placeholder="e.g. 'I only take trades with 2:1 RR minimum and never before FOMC…'" rows={3} className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--border-focus)] resize-none" />
            <button data-testid="add-mem-btn" onClick={addMemory} disabled={busy === "mem"} className="mt-2 bg-white text-black px-4 py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40">{busy === "mem" ? "SAVING…" : "SAVE MEMORY"}</button>
          </div>
          <div className="border border-[color:var(--border-default)]">
            {mems.length === 0 ? (
              <div className="p-4 text-xs text-[color:var(--text-muted)] font-mono-data">No memory yet.</div>
            ) : (
              mems.map((m) => (
                <div key={m.id} className="flex items-start justify-between gap-3 px-3 py-2 border-b border-[color:var(--border-default)] last:border-0" data-testid={`mem-${m.id}`}>
                  <div>
                    <div className="text-[10px] font-mono-data text-gold tracking-widest">{m.memory_type?.toUpperCase()}</div>
                    <div className="text-sm text-white mt-1">{m.text}</div>
                    <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">{(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <button onClick={() => delMem(m.id)} className="text-[10px] font-mono-data text-bear border border-bear px-2 py-1 hover:bg-bear hover:text-white transition-colors">DELETE</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
